import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { LuChevronDown, LuShieldCheck, LuX } from "react-icons/lu";
import type { HomePhase } from "@/components/v2/home/types";
import type { PollV2 } from "@/lib/api/polls";
import { buildPollV2Aggregates } from "@/lib/api/polls";
import { topSlots } from "@/lib/v2/pollAggregates";
import {
  formatHourLabel,
  formatPollCountdown,
} from "@/lib/v2/pollAggregates";
import { cn } from "@/lib/utils";

interface HomeAdminPhaseHintProps {
  phase: HomePhase;
  pollV2?: PollV2 | null;
  pollOpen?: boolean;
  className?: string;
}

function dismissKey(phase: HomePhase) {
  return `v2.home.adminHint.dismissed.${phase}`;
}

/**
 * Compact admin-only rail under the week tracker — collapsed by default on
 * mobile so the phase card stays above the fold; tap to expand or dismiss.
 */
export function HomeAdminPhaseHint({
  phase,
  pollV2,
  pollOpen = false,
  className,
}: HomeAdminPhaseHintProps) {
  const { t } = useTranslation();
  const dateLocale = es;
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem(dismissKey(phase)) === "1";
  });

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    setDismissed(sessionStorage.getItem(dismissKey(phase)) === "1");
    setExpanded(false);
  }, [phase]);

  const pollStats = useMemo(() => {
    if (!pollV2) return null;
    const { aggregates, totalVoters } = buildPollV2Aggregates(pollV2);
    const top = topSlots(aggregates, 1)[0];
    if (!top) return { totalVoters, topLabel: null as string | null };
    const dayName = format(parseISO(`${top.day}T12:00:00`), "EEEE", { locale: dateLocale });
    const topLabel = `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${formatHourLabel(top.hour)}`;
    return { totalVoters, topLabel };
  }, [pollV2, dateLocale]);

  const closesMs = pollV2?.closes_at ? parseISO(pollV2.closes_at).getTime() : 0;
  const [remainingMs, setRemainingMs] = useState(() =>
    pollV2?.closes_at ? closesMs - Date.now() : 0
  );

  useEffect(() => {
    if (!pollV2?.closes_at) return;
    const id = setInterval(() => setRemainingMs(closesMs - Date.now()), 1000);
    return () => clearInterval(id);
  }, [pollV2?.closes_at, closesMs]);

  const pollClosed = !pollV2?.closes_at || remainingMs <= 0;

  const message = (() => {
    switch (phase) {
      case "poll_inactive":
        return t("v2.home.adminHintIdle");
      case "poll_open":
        if (pollStats) {
          const parts = [
            t("v2.home.adminHintPollOpen", { count: pollStats.totalVoters }),
            t("v2.home.adminHintPollReshare"),
          ];
          if (pollStats.topLabel) {
            parts.push(t("v2.home.adminHintPollTop", { slot: pollStats.topLabel }));
          }
          if (pollV2?.closes_at) {
            parts.push(
              pollClosed
                ? t("v2.home.adminHintPollClosed")
                : t("v2.home.adminHintPollCloses", {
                    time: formatPollCountdown(remainingMs),
                  })
            );
          }
          return parts.join(" · ");
        }
        return pollOpen
          ? `${t("v2.home.adminHintPollOpenGeneric")} · ${t("v2.home.adminHintPollReshare")}`
          : t("v2.home.adminHintIdle");
      case "convocatoria":
        return t("v2.home.adminHintConvocatoria");
      case "post_kickoff":
        return t("v2.home.adminHintPostKickoff");
      case "completed":
        return t("v2.home.adminHintCompleted");
      default:
        return null;
    }
  })();

  const actionRequired = phase === "poll_inactive" || phase === "poll_open" || phase === "completed";

  const shortMessage = useMemo(() => {
    if (!message) return null;
    const first = message.split(" · ")[0];
    return first.length > 48 ? `${first.slice(0, 45)}…` : first;
  }, [message]);

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey(phase), "1");
    setDismissed(true);
  };

  if (!message || dismissed) return null;

  if (!expanded) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.05] px-3 py-2",
          className
        )}
        role="status"
      >
        <LuShieldCheck
          className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="min-h-[44px] flex-1 truncate text-left text-xs text-foreground/90"
        >
          {actionRequired && (
            <span className="mr-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
          )}
          {shortMessage}
          <span className="ml-1 font-semibold text-amber-700 dark:text-amber-300">
            · {t("v2.home.adminHintExpand")}
          </span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/50"
          aria-label={t("common.dismiss")}
        >
          <LuX className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2",
        className
      )}
      role="status"
    >
      <LuShieldCheck
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
            {t("v2.home.adminZoneTitle")}
            {actionRequired && (
              <span className="ml-1.5 inline-flex items-center gap-1 normal-case tracking-normal text-amber-700 dark:text-amber-200">
                <span className="relative flex h-1.5 w-1.5 motion-reduce:animate-none" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75 motion-reduce:animate-none" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-600" />
                </span>
                {t("v2.home.adminActionRequired")}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/50"
            aria-label={t("common.dismiss")}
          >
            <LuChevronDown className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs leading-snug text-foreground/90">{message}</p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/50"
        aria-label={t("common.dismiss")}
      >
        <LuX className="h-4 w-4" />
      </button>
    </div>
  );
}
